import { Bot, FolderKanban, GitCommitHorizontal } from "lucide-react";

import { useStudioLocale } from "@/i18n";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import { useStudioStore } from "@/store/studio-store";

const COPY: Record<
  LocaleCode,
  {
    projectContext: string;
    botContext: string;
    ideConnected: string;
    noProjectRequired: string;
    targetRevision: string;
    sourceRevision: string;
    currentSavedState: string;
    unsavedProject: string;
    botDetail: string;
  }
> = {
  en: {
    projectContext: "Project chat",
    botContext: "Independent bot",
    ideConnected: "IDE connected",
    noProjectRequired: "No project required",
    targetRevision: "Target revision",
    sourceRevision: "Source revision",
    currentSavedState: "current saved state",
    unsavedProject: "Unsaved project",
    botDetail:
      "Ask anything here. Generated artifacts remain in this conversation while this screen is open, until project save and open are available.",
  },
  ko: {
    projectContext: "프로젝트 Chat",
    botContext: "독립 Bot",
    ideConnected: "IDE 연결됨",
    noProjectRequired: "프로젝트 없이 사용",
    targetRevision: "대상 리비전",
    sourceRevision: "소스 리비전",
    currentSavedState: "현재 저장 상태",
    unsavedProject: "저장되지 않은 프로젝트",
    botDetail:
      "여기서 무엇이든 요청할 수 있습니다. 생성 결과는 프로젝트 저장·열기 연결 전까지 이 화면을 열어 둔 동안 Bot 대화에 남습니다.",
  },
  ja: {
    projectContext: "プロジェクト Chat",
    botContext: "独立 Bot",
    ideConnected: "IDE 接続済み",
    noProjectRequired: "プロジェクト不要",
    targetRevision: "対象リビジョン",
    sourceRevision: "ソースリビジョン",
    currentSavedState: "現在の保存状態",
    unsavedProject: "未保存のプロジェクト",
    botDetail:
      "ここでは何でも依頼できます。生成物はプロジェクトへの保存とオープンが利用可能になるまで、この画面を開いている間 Bot 会話に保持されます。",
  },
  zh: {
    projectContext: "项目 Chat",
    botContext: "独立 Bot",
    ideConnected: "已连接 IDE",
    noProjectRequired: "无需项目",
    targetRevision: "目标修订",
    sourceRevision: "源修订",
    currentSavedState: "当前保存状态",
    unsavedProject: "未保存项目",
    botDetail:
      "你可以在这里提出任何请求。在此页面保持打开期间，生成内容会保留在 Bot 对话中，直到项目保存和打开功能可用。",
  },
  "zh-TW": {
    projectContext: "專案 Chat",
    botContext: "獨立 Bot",
    ideConnected: "已連接 IDE",
    noProjectRequired: "無需專案",
    targetRevision: "目標修訂",
    sourceRevision: "來源修訂",
    currentSavedState: "目前儲存狀態",
    unsavedProject: "未儲存專案",
    botDetail:
      "你可以在這裡提出任何請求。在此畫面保持開啟期間，生成內容會保留在 Bot 對話中，直到專案儲存與開啟功能可用。",
  },
};

interface ConversationScopeBarProps {
  mode: "bot" | "project";
  title: string;
  target: string;
  detail?: string;
  projectId?: string | null;
  className?: string;
}

export function ConversationScopeBar({
  mode,
  title,
  target,
  detail,
  projectId,
  className = "",
}: ConversationScopeBarProps) {
  const { locale } = useStudioLocale();
  const copy = COPY[locale];
  const Icon = mode === "project" ? FolderKanban : Bot;

  return (
    <section
      data-testid={`conversation-scope-${mode}`}
      data-project-id={projectId ?? undefined}
      className={`flex min-w-0 items-start gap-2.5 rounded-md border border-border bg-muted/25 px-3 py-2 ${className}`}
    >
      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[11px] font-medium text-muted-foreground">
            {mode === "project" ? copy.projectContext : copy.botContext}
          </span>
          <span className="truncate text-sm font-semibold text-foreground">{title}</span>
          <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
            {mode === "project" ? copy.ideConnected : copy.noProjectRequired}
          </span>
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          <GitCommitHorizontal className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="truncate">{target}</span>
        </div>
        {detail ? <p className="mt-1 text-[11px] leading-4 text-muted-foreground">{detail}</p> : null}
      </div>
    </section>
  );
}

export function ProjectConversationScopeBar({ className }: { className?: string }) {
  const { locale } = useStudioLocale();
  const copy = COPY[locale];
  const currentProjectId = useStudioStore((state) => state.currentProjectId);
  const currentProject = useStudioStore((state) => state.currentProject);
  const projectMeta = useStudioStore((state) => state.projectMeta);
  const sourceRevision = useStudioStore((state) => state.chatContext.sourceRevision);
  const manifest = currentProject?.manifest as { name?: unknown } | undefined;
  const projectName =
    projectMeta?.name ??
    (typeof manifest?.name === "string" && manifest.name.trim().length > 0
      ? manifest.name
      : copy.unsavedProject);
  const revision = projectMeta?.version;

  return (
    <ConversationScopeBar
      mode="project"
      title={projectName}
      target={`${copy.targetRevision}: ${revision ?? copy.currentSavedState}`}
      detail={
        sourceRevision === undefined
          ? undefined
          : `${copy.sourceRevision}: ${sourceRevision}`
      }
      projectId={currentProjectId}
      className={className}
    />
  );
}

export function BotConversationScopeBar({
  botName,
  className,
}: {
  botName: string;
  className?: string;
}) {
  const { locale } = useStudioLocale();
  const copy = COPY[locale];
  return (
    <ConversationScopeBar
      mode="bot"
      title={botName}
      target={copy.noProjectRequired}
      detail={copy.botDetail}
      className={className}
    />
  );
}
